import { ChangeDetectionStrategy, Component, Inject, OnInit, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import {
    EmployeeLeavingRequestDTO,
    EmployeeOverviewView,
    ScheduledLeavingRequestView,
    ScheduleEmployeeLeavingRequest,
    UpdateEmployeeCompanyLeavingDateRequest
} from '@iota/inh-core-v1-management-client';
import { IotaModalSize, MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { BehaviorSubject, debounceTime, distinctUntilChanged } from 'rxjs';
import { getFirstActionValue, getValueFromStore, waitForBooleanValue } from '../../utils';
import { ConfirmationDialogData, DialogConfirmationComponent } from '../dialog-confirmation/dialog-confirmation.component';
import { selectedCompanyOfficeIdToNameMap } from 'src/app/store/store';
import {
    employeeLeavingActionError,
    employeeLeavingActionLoading,
    employeeLeavingRequestById
} from 'src/app/store/reducer/employee-leaving.reducer';
import {
    CalculateLicenseEndDate,
    CalculateLicenseEndDateFailure,
    CalculateLicenseEndDateSuccess,
    CancelEmployeeLeaving,
    ScheduleEmployeeLeaving,
    UpdateEmployeeLeavingDate
} from 'src/app/store/actions/employee-leaving.actions';
import { Actions } from '@ngrx/effects';

export interface EmployeeLeavingDialogData {
    employeeLeavingRequestId?: string;
    employeeLeavingRequest?: ScheduledLeavingRequestView;
    companyEmployeeId?: string;
    employee?: EmployeeOverviewView;
}

@Component({
    standalone: false,
    selector: 'app-employee-leaving-dialog',
    templateUrl: './employee-leaving-dialog.component.html',
    styleUrls: ['./employee-leaving-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmployeeLeavingDialogComponent implements OnInit {
    public modalOptions = {
        size: IotaModalSize.SIZE_3XL
    };

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: EmployeeLeavingDialogData,
        private dialogRef: MatDialogRef<EmployeeLeavingDialogComponent>,
        private store: Store,
        private actions$: Actions,
        private formBuilder: FormBuilder,
        private dialog: MatDialog
    ) {}

    private readonly selectedLeavingRequestState = signal<EmployeeLeavingRequestDTO>(undefined);
    readonly selectedLeavingRequest = this.selectedLeavingRequestState.asReadonly();

    cancelLeavingPressed = false;
    savePressed = false;

    actionLoading$ = this.store.select(employeeLeavingActionLoading);
    actionError$ = this.store.select(employeeLeavingActionError);
    officeIdToNameMap$ = this.store.select(selectedCompanyOfficeIdToNameMap);

    form = this.formBuilder.group({
        companyLeavingDate: [null as string, [Validators.required]]
    });

    calculatedLicenseEndDate$ = new BehaviorSubject<string | null>(null);
    licenseEndDateCalculationLoading$ = new BehaviorSubject<boolean>(false);

    ngOnInit(): void {
        this.initDialog();

        this.form
            .get('companyLeavingDate')
            .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
            .subscribe(async (companyLeavingDate) => {
                if (!companyLeavingDate) return;
                this.licenseEndDateCalculationLoading$.next(true);

                this.store.dispatch(CalculateLicenseEndDate({ companyLeavingDate }));
                const response = await getFirstActionValue(this.actions$, CalculateLicenseEndDateSuccess, CalculateLicenseEndDateFailure);
                this.calculatedLicenseEndDate$.next(response?.licenseEndDate);
                this.licenseEndDateCalculationLoading$.next(false);
            });
    }

    async initDialog() {
        if (this.data?.employeeLeavingRequestId) {
            const leavingRequest =
                this.data.employeeLeavingRequest ??
                (await getValueFromStore(this.store, employeeLeavingRequestById(this.data.employeeLeavingRequestId)));
            this.selectedLeavingRequestState.set(leavingRequest);
            this.form.patchValue({
                companyLeavingDate: leavingRequest?.companyLeavingDate
            });
            this.calculatedLicenseEndDate$.next(leavingRequest?.licenseEndDate);

            if (!leavingRequest?.canBeModified) {
                this.form.disable();
            }
        }
    }

    cancelLeaving() {
        const dialogData: ConfirmationDialogData = {
            title: $localize`Cancel leaving`,
            text: $localize`Are you sure you want to cancel this employee leaving process?`,
            cancelButtonClass: 'btn-outline',
            confirmButtonStyle: 'primary'
        };

        this.dialog
            .open(DialogConfirmationComponent, {
                data: dialogData
            })
            .afterClosed()
            .subscribe(async (confirmed) => {
                if (confirmed) {
                    this.savePressed = false;
                    this.cancelLeavingPressed = true;
                    this.store.dispatch(CancelEmployeeLeaving({ id: this.data?.employeeLeavingRequestId }));
                    this.waitForActionAndCloseDialogOnSuccess();
                }
            });
    }

    closeDialog() {
        this.dialogRef.close();
    }

    get showCancelButton() {
        return !!this.data?.employeeLeavingRequestId && this.selectedLeavingRequest()?.canBeModified;
    }

    get isCancelButtonDisabled() {
        return !this.selectedLeavingRequest()?.canBeModified;
    }

    get showSaveButton() {
        return !this.data?.employeeLeavingRequestId || this.selectedLeavingRequest()?.canBeModified;
    }

    get isSaveButtonDisabled() {
        const formInvalid = !this.form?.valid;
        const cannotBeModifiedAnymore = !!this.data?.employeeLeavingRequestId && !this.selectedLeavingRequest()?.canBeModified;
        return formInvalid || cannotBeModifiedAnymore;
    }

    get isScheduledAndCannotBeModified() {
        const leavingRequest = this.selectedLeavingRequest();
        return (
            !!leavingRequest && leavingRequest.status === EmployeeLeavingRequestDTO.StatusEnum.SCHEDULED && !leavingRequest.canBeModified
        );
    }

    async save() {
        this.cancelLeavingPressed = false;
        this.savePressed = true;

        if (!this.data?.employeeLeavingRequestId) {
            const newLeavingRequest: ScheduleEmployeeLeavingRequest = {
                ...this.form.getRawValue(),
                employeeId: this.data?.companyEmployeeId
            };
            this.store.dispatch(ScheduleEmployeeLeaving({ request: newLeavingRequest }));
        } else {
            const updateLeavingRequest: UpdateEmployeeCompanyLeavingDateRequest = {
                newCompanyLeavingDate: this.form.get('companyLeavingDate').value
            };
            this.store.dispatch(UpdateEmployeeLeavingDate({ id: this.data.employeeLeavingRequestId, request: updateLeavingRequest }));
        }

        this.waitForActionAndCloseDialogOnSuccess();
    }

    async waitForActionAndCloseDialogOnSuccess() {
        await waitForBooleanValue(this.store, employeeLeavingActionLoading, false);
        const actionError = await getValueFromStore(this.store, employeeLeavingActionError);

        if (!actionError) {
            this.closeDialog();
        }
    }
}
